import { FormGroup } from '@carbon/react';
import { DatePicker } from 'components/date-picker';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import styles from './created-at-filter.module.scss';

interface DateRange {
  start?: string;
  end?: string;
}

interface CreatedAtFilterProps {
  onChange: (dateRange?: DateRange) => void;
  formattedDateRange?: {
    start: string | undefined;
    end: string | undefined;
  };
}

export const CreatedAtFilter: React.FC<CreatedAtFilterProps> = ({
  onChange,
  formattedDateRange,
}) => {
  const [startDate, setStartDate] = useState<string>(
    formattedDateRange?.start || ''
  );
  const [endDate, setEndDate] = useState<string>(formattedDateRange?.end || '');
  const [startError, setStartError] = useState<string>('');
  const [endError, setEndError] = useState<string>('');

  // Update local state when formattedDateRange changes
  useEffect(() => {
    if (formattedDateRange) {
      setStartDate(formattedDateRange.start || '');
      setEndDate(formattedDateRange.end || '');
    } else {
      setStartDate('');
      setEndDate('');
    }
  }, [formattedDateRange]);

  const validateDate = (dateStr: string): boolean => {
    if (!dateStr) return true;
    return moment(dateStr, 'YYYY-MM-DD', true).isValid();
  };
  const notifyChange = useCallback(() => {
    if (!startDate || !endDate) {
      onChange(undefined);
      return;
    }

    const start = moment(startDate, 'YYYY-MM-DD', true);
    const end = moment(endDate, 'YYYY-MM-DD', true);

    if (!start.isValid() || !end.isValid()) {
      return;
    }

    if (end.isBefore(start)) {
      setEndError('End date must be after start date');
      return;
    }

    // Get current UTC time components once
    const now = moment.utc();
    const timeSettings = {
      hour: now.hour(),
      minute: now.minute(),
      second: now.second(),
      millisecond: now.millisecond(),
    };

    onChange({
      start: moment.utc(startDate).set(timeSettings).toISOString(),
      end: moment.utc(endDate).set(timeSettings).toISOString(),
    });
  }, [startDate, endDate, onChange]);

  useEffect(() => {
    notifyChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const handleStartDateChange = (dates: Date[]) => {
    // Carbon DatePicker sends an empty array when clicking the field without selecting
    if (!dates || dates.length === 0) {
      // Clear date case - user actively cleared the date
      if (startDate) {
        setStartDate('');
        setStartError('');
      }
    }

    // A date was actually selected
    const dateStr = moment(dates[0]).format('YYYY-MM-DD');
    if (!validateDate(dateStr)) {
      setStartError('Invalid date format');
      return;
    }

    // Only update if the value has changed
    if (dateStr !== startDate) {
      setStartError('');
      setStartDate(dateStr);
      const start = moment(startDate);
      const end = moment(endDate);
      if (!end.isBefore(start)) {
        setEndError('');
      }
    }
  };

  const handleEndDateChange = (dates: Date[]) => {
    // Carbon DatePicker sends an empty array when clicking the field without selecting
    if (!dates || dates.length === 0) {
      // Clear date case - user actively cleared the date
      if (endDate) {
        setEndDate('');
        setEndError('');
      }
      return;
    }

    // A date was actually selected
    const dateStr = moment(dates[0]).format('YYYY-MM-DD');
    if (!validateDate(dateStr)) {
      setEndError('Invalid date format');
      return;
    }

    // Check if end date is after start date (if start date exists)
    if (startDate) {
      const start = moment(startDate);
      const end = moment(dateStr);
      if (end.isBefore(start)) {
        setEndError('End date must be after start date');
        return;
      }
    }
    // Only update if the value has changed
    if (dateStr !== endDate) {
      setEndError('');
      setEndDate(dateStr);
    }
  };

  return (
    <FormGroup legendText="Created At">
      <div className={styles.createdAtFilter}>
        <DatePicker
          id="start-date"
          labelText="Start Date"
          value={startDate}
          onChange={handleStartDateChange}
          invalid={!!startError}
          invalidText={startError}
        />

        <DatePicker
          id="end-date"
          labelText="End Date"
          value={endDate}
          onChange={handleEndDateChange}
          invalid={!!endError}
          invalidText={endError}
        />
      </div>
    </FormGroup>
  );
};
